import type { RendererController } from '@renderer/index';
import type { AssetItem } from '@stores/assetLibraryStore';
import type { Transform, ViewerModelHandle } from '@mindcloudxai_3d/core';

/**
 * 将主视窗中的模型重放到目标 controller（预览/导出）。
 * 逐个导入模型并应用当前 transform，返回 mainId → targetId 映射，
 * 供后续 transform/model:changed 事件同步使用。
 */
export async function replayModels(
  targetController: RendererController,
  assets: AssetItem[],
): Promise<Map<string, string>> {
  const mapping = new Map<string, string>();

  // 先清除目标 controller 上已有的模型
  const existing = targetController.model.getModels();
  for (const m of existing) {
    targetController.model.removeModel(m.id);
  }

  // 逐个导入模型并应用 transform
  for (const asset of assets) {
    if (!asset.source) continue;
    try {
      const handle = await targetController.model.addModel({
        url: asset.source.url,
        file: asset.source.file,
        name: asset.name,
      });
      targetController.model.setModelTransform(handle.id, asset.transform);
      mapping.set(asset.id, handle.id);
    } catch (err) {
      console.error('Failed to replay model:', asset.name, err);
    }
  }

  return mapping;
}

/**
 * 增量同步模型列表变化（新增/删除），不触动已有模型。
 * 在 model:changed 事件中使用。
 */
export function syncModelList(
  mainModels: ViewerModelHandle[],
  targetController: RendererController,
  mapping: Map<string, string>,
  assets: AssetItem[],
  pending = new Set<string>(),
): void {
  const mainIds = new Set(mainModels.map((m) => m.id));

  // 删除主视窗中已不存在的模型
  for (const [mainId, targetId] of mapping) {
    if (!mainIds.has(mainId)) {
      targetController.model.removeModel(targetId);
      mapping.delete(mainId);
    }
  }
  for (const mainId of pending) {
    if (!mainIds.has(mainId)) {
      pending.delete(mainId);
    }
  }

  // 新增主视窗中新加的模型
  for (const m of mainModels) {
    if (mapping.has(m.id) || pending.has(m.id)) continue;
    const asset = assets.find((a) => a.id === m.id);
    if (!asset?.source) continue;
    pending.add(m.id);
    void targetController.model
      .addModel({
        url: asset.source.url,
        file: asset.source.file,
        name: m.name,
      })
      .then((handle) => {
        if (!pending.has(m.id)) {
          targetController.model.removeModel(handle.id);
          return;
        }
        targetController.model.setModelTransform(handle.id, m.transform);
        mapping.set(m.id, handle.id);
      })
      .catch((err) => console.error('Failed to sync new model:', err))
      .finally(() => pending.delete(m.id));
  }
}

/**
 * 同步单个模型 transform 变化到目标 controller。
 * 在 model:transform 事件中使用。
 */
export function syncModelTransform(
  modelId: string,
  transform: Transform,
  targetController: RendererController,
  mapping: Map<string, string>,
): void {
  const targetId = mapping.get(modelId);
  if (targetId) {
    targetController.model.setModelTransform(targetId, transform);
  }
}
